import { defineComponent as _defineComponent } from 'vue'
import Skeleton from './loaders/Skeleton.vue'
import DropDownMenu from './dropdowns/DropDownMenu.vue'
import SortDropDown from './dropdowns/SortDropDown.vue'
import ButtonCommon from "./buttons/ButtonCommon.vue"
import TooltippedText from './tooltip/TooltippedText/TooltippedText.vue'
import Tooltip from './tooltip/Tooltip.vue'
import CheckBox from './selection-controls/CheckBox.vue'
import { computed, onBeforeUpdate, ref, watch } from 'vue'
import vuetify from '../service/useVuetify';

type Item = {
  id: number,
  children?: [],
  isChild?: boolean,
  isItemSelectable?: boolean,
}

type PageMenuItem = {
  text: string,
  value: string,
}

type Pagination = {
  pageStart: number;
  pageStop: number;
  itemsLength: number;
  page: number;
  pageCount: number;
}

type Option = {
  page: number,
  itemsPerPage: number,
  sortBy: string[],
  sortDesc: boolean[],
  groupBy: string[],
  groupDesc: boolean[],
  mustSort: boolean,
  multiSort: boolean,
}


export default /*#__PURE__*/_defineComponent({
  __name: 'Table',
  props: {
    data: { default: () => [] },
    loading: { type: Boolean },
    headers: { default: () => [] },
    hasActions: { type: Boolean },
    previousTooltip: { default: '' },
    nextTooltip: { default: '' },
    selectable: { type: Boolean, default: false },
    selectedRows: { default: () => [] },
    moreTooltip: { default: '' },
    parentIdPropertyName: { default: '' },
    itemsPerPage: { default: 25 },
    serverItemsLength: null,
    search: null,
    expandAllRows: { type: Boolean, default: false },
    isNoTotalSize: { type: Boolean, default: false },
    noResultsText: null,
    itemsPerPageOptions: { default: () => [25, 50, 100] },
    rowHeight: { default: 'small' }
  },
  emits: ["row-select", "pagination", "options"],
  setup(__props: any, { expose, emit }: { emit: ({
  (e: string, item?: Item): void;
  (e: 'row-select', item: number | number[]): void;
  (e: 'pagination', pagination: Pagination): void;
  (e: 'options', option: Option): void;
}), expose: any, slots: any, attrs: any }) {

const props = __props as { data: Array<Item>, loading?: boolean, headers: Array<{
      skeletonWidth?: string,
      justify?: string,
      text?: string,
      value?: string,
      sortable?: boolean,
    }>, hasActions?: boolean, previousTooltip: string, nextTooltip: string, selectable: boolean, selectedRows: Array<any>, moreTooltip: string, parentIdPropertyName: string, itemsPerPage: number, serverItemsLength?: number, search?: string, expandAllRows: boolean, isNoTotalSize: boolean, noResultsText?: string, itemsPerPageOptions: Array<number>, rowHeight: 'small' | 'large' };



const page = ref(1);
const refs = ref<string[]>([]);
const pageCount = ref<number>(0);
const itemsPerPage = ref(props.itemsPerPage);
const itemsPerPageOption = ref<PageMenuItem>({text: itemsPerPage.value.toString(), value: itemsPerPage.value.toString()});
const itemsPerPageOptions = ref(props.itemsPerPageOptions);
const pageStart = ref<number | null>(null);
const pageStop = ref<number | null>(null);
const itemsLength = ref<number | null>(null);
const selectedRow = ref<number | null>(null);
const hoverRowId = ref<number | null>(null);
const key = ref(0);
const expanded = ref<number[]>([]);
const displayedItems = ref([]);



watch(
  () => props.expandAllRows,
  () => {
    props.expandAllRows ? props.data.forEach((item) => !item.isChild && expanded.value.push(item.id)) : (expanded.value = []);
  }
);

const prevPage = computed(() => {
  const result = page.value - 1
  if (result < 1) return 1
  else return result
})
const nextPage = computed(() => {
  const result = page.value + 1
  if (result > pageCount.value!) return result - 1
  else return result
})
const denseRows = computed(() => { //rows are dense on larger breakpoints
  return vuetify.value.breakpoint.name === "xl"
})
const itemsPerPageMenuOptions = computed(() => {
  let paginationOption: number[] = [];
  let firstBiggerThanItemsLengthAdded = false;
  const itemsLength = props.serverItemsLength || tableData.value.length;

  if (props.isNoTotalSize) {
    paginationOption = itemsPerPageOptions.value;
  } else {
    itemsPerPageOptions.value.forEach(function(optionValue) {
      if (itemsLength >= optionValue) {
        paginationOption.push(optionValue);
      } else if (itemsLength < optionValue && !firstBiggerThanItemsLengthAdded) {
        paginationOption.push(optionValue);
        firstBiggerThanItemsLengthAdded = true;
      }
    })
  }

  return paginationOption.map(item => {
    return { text: item.toString(), value: item.toString() }
  });
})
const tableData = computed(() => {
  return props.data.filter(da => (expanded.value.includes((da as any)[props.parentIdPropertyName]) && da.isChild) || !da.isChild);
})
const tableHeaders = computed(() => {
  let headers = props.headers;
  if(props.hasActions) {
    headers = props.headers.concat({ sortable: false })
  }
  return headers
})
const itemsLengthLabel = computed(() => props.isNoTotalSize ? `${itemsLength.value}+` : itemsLength.value);
function emitOptions(option: Option) {
  emit('options', option);
}
function onCheckBoxHover(rowId: number | null) {
  hoverRowId.value = rowId;
}
onBeforeUpdate(() => {
  refs.value = [];
})
function selectRow(value: boolean, id: number, rowProp = null) { //row becomes grey when having its menu open
  // rowProp could be sent if this method is used in the exapanded items
  if (value) {
    selectedRow.value = id;
  } else {
    if (selectedRow.value === id) selectedRow.value = null
  }
}
function selectRowItem(item: number) {
  emit('row-select', item);
}
function selectAll(value: boolean) {
  emit('row-select', value ? tableData.value.map(td => td.id) : []);
}

function updatePaginationData(pagination: Pagination) {
  pageStart.value = pagination.pageStart + 1
  pageStop.value = pagination.pageStop
  itemsLength.value = pagination.itemsLength
  page.value = pagination.page > pagination.pageCount ? pagination.pageCount : pagination.page;
  emit('pagination', pagination);
}
function expandChildren(item: Item) {
  if (item.children && item.children.length) {
    if (expanded.value.includes(item.id)) {
      // remove this item from the expanded array
      expanded.value = expanded.value.filter(e => e !== item.id)
    } else {
      // add this item to the expanded array
      expanded.value = expanded.value.concat(item.id);
    }
  }
}
function emitSelect(value: string | number | boolean | object, item: Item) {
  emit(value as string, item);
}
function selectEvent(item: PageMenuItem) {
  itemsPerPage.value = parseInt(item.value);
  itemsPerPageOption.value = item;
  page.value = 1;
}

function resetToFirstPage() {
  page.value = 1;
}

expose({
  resetToFirstPage
});


return { __sfc: true,props, page, refs, pageCount, itemsPerPage, itemsPerPageOption, itemsPerPageOptions, pageStart, pageStop, itemsLength, selectedRow, hoverRowId, key, expanded, displayedItems, emit, prevPage, nextPage, denseRows, itemsPerPageMenuOptions, tableData, tableHeaders, itemsLengthLabel, emitOptions, onCheckBoxHover, selectRow, selectRowItem, selectAll, updatePaginationData, expandChildren, emitSelect, selectEvent, resetToFirstPage, Skeleton, DropDownMenu, SortDropDown, ButtonCommon, TooltippedText, Tooltip, CheckBox }
}

})